import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Lock, LockOpen } from 'lucide-react';
import CardShell from '../components/CardShell';
import Listbox from '../components/Listbox';
import { Cell, Choix } from '../components/InlineCells';
import { ReportingContext } from '../components/ReportingControls';
import { useP } from '../i18n/useP';
import { formatDate } from '../utils/format';
import {
  listPlanVersions, createPlanVersion, patchPlanVersion, deletePlanVersion,
} from '../api/planVersions';
import './PlanVersionsPage.css';

/* PARAMÈTRES › PLANIFICATION — les versions de plan (décision Olivier,
   2026-09-27). Une version garde TOUTES les saisies de planification ; elle
   naît par DUPLICATION d'une autre (jamais vide), se verrouille quand elle
   fait foi, et UNE seule est courante : celle que les modules ouvrent par
   défaut. Créer, verrouiller, désigner, supprimer relève de l'administrateur
   et du financier du client ; les autres lisent la liste.

   TYPE, PÉRIODE, HORIZON ET BUDGET DE RÉFÉRENCE (décisions d'Olivier du
   2026-10-11, proto `WIP/protos/versions-de-plan.html`) : une version est un
   BUDGET (0 + 12), une PRÉVISION n + m (n mois réalisés, m planifiés) ou une
   PRÉVISION GLISSANTE, qui avance à chaque mois clôturé ; elle part d'un
   exercice et court sur son horizon. Un seul BUDGET DE RÉFÉRENCE, celui que
   lisent les rapports. Le type, l'exercice et l'horizon se choisissent dans la
   fenêtre de création ou de duplication, et se changent tant que la version
   est ouverte. */
const MANAGER_ROLES = ['admin', 'finance'];
/* LA COULEUR DE REPÈRE (demande d'Olivier, 2026-09-28) : elle teinte la
   pastille de la version dans la bande de contexte. Des clés de la charte
   (`--color-plan-*`), l'accent du thème par défaut. Elle se lit en pastille
   devant le nom ; un clic propose les cinq (2026-10-11, la table gagnait une
   colonne). */
const PLAN_COLORS = [
  { key: 'slate', label: 'Couleur du thème' },
  { key: 'sage', label: 'Sauge' },
  { key: 'amber', label: 'Ambre' },
  { key: 'clay', label: 'Terre cuite' },
  { key: 'navy', label: 'Nuit' },
];
const HORIZON_MAX = 10;
// Le type dans la liste de la fenêtre : `null` = glissante.
const GLISSANTE = 'rolling';

export default function PlanVersionsPage({ activeTenant, addToast, roles = [], authUser }) {
  const p = useP();
  const ctx = useContext(ReportingContext);
  const tenantId = activeTenant?.id;
  const canManage = Boolean(authUser?.is_platform_admin) || roles.some((r) => MANAGER_ROLES.includes(r));
  const [rows, setRows] = useState([]);
  const [fenetre, setFenetre] = useState(null);   // { mode: 'new' | 'dup' | 'edit', ... }
  const [palette, setPalette] = useState(null);   // la version dont on choisit la couleur

  const load = useCallback(async () => {
    if (!tenantId) return;
    const res = await listPlanVersions(tenantId);
    setRows(res.data || []);
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const done = async (fn) => {
    try {
      await fn();
      await load();
      if (ctx?.reloadVersions) ctx.reloadVersions();
      return true;
    } catch (e) {
      addToast && addToast(e.message || String(e), 'error');
      return false;
    }
  };

  const current = rows.find((v) => v.is_current);
  /* Le nom du type : Budget, Budget de référence, Prévision n + m, ou
     Prévision glissante avec son découpage du jour. */
  const nomType = (n) => (n == null ? p('Prévision glissante') : n === 0 ? p('Budget') : `${p('Prévision')} ${n} + ${12 - n}`);
  const typeLu = (v) => {
    if (v.actual_months == null) {
      return <>{p('Prévision glissante')} <small className="pvp-muted">· {v.actual_months_now} + {12 - v.actual_months_now}</small></>;
    }
    if (v.actual_months === 0) return v.is_reference ? <span className="pvp-ref">{p('Budget de référence')}</span> : p('Budget');
    return nomType(v.actual_months);
  };

  /* LA FENÊTRE : créer (copie de la courante), dupliquer (copie de la ligne)
     ou modifier ; une duplication reprend les réglages de sa source, tous
     modifiables. */
  const ouvrir = (mode, src) => setFenetre({
    mode, id: src.id, label: mode === 'edit' ? src.label : mode === 'dup' ? `${src.label} (${p('copie')})` : '',
    source: src.id, type: src.actual_months == null ? GLISSANTE : src.actual_months,
    start: src.start_year, horizon: src.horizon_years,
  });
  const valider = async () => {
    const f = fenetre;
    const reglages = { actual_months: f.type === GLISSANTE ? null : f.type, start_year: f.start, horizon_years: f.horizon };
    const ok = await done(() => (f.mode === 'edit'
      ? patchPlanVersion(tenantId, f.id, { label: f.label, ...reglages })
      : createPlanVersion(tenantId, { label: f.label, source_id: f.source, ...reglages })));
    if (ok) setFenetre(null);
  };
  const exercice = current ? current.start_year : new Date().getFullYear();
  const annees = Array.from({ length: 6 }, (_, k) => exercice - 2 + k);
  const types = [{ value: 0, t: p('Budget'), s: '0 + 12' },
    ...Array.from({ length: 11 }, (_, k) => ({ value: k + 1, t: nomType(k + 1), s: `${k + 1} ${p('mois réalisés')}, ${11 - k} ${p('planifiés')}` })),
    { value: GLISSANTE, t: p('Prévision glissante'), s: p('avance à chaque mois clôturé') }];

  return (
    <div className="pvp">
      <CardShell
        title={p('Versions de plan')}
        primaryAction={canManage && current ? (
          <button type="button" className="lk-btn" onClick={() => ouvrir('new', current)}>
            ＋ {p('Version')}
          </button>
        ) : null}
        flush
      >
        <div className="lk-hscroll">
        <table className="lk-entry-table pvp-tbl">
          <colgroup>
            <col style={{ width: 260 }} />
            <col style={{ width: 215 }} />
            <col style={{ width: 105 }} />
            <col style={{ width: 115 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 28 }} />
          </colgroup>
          <thead>
            <tr>
              <th>{p('Nom')}</th>
              <th>{p('Type')}</th>
              <th>{p('Période')}</th>
              <th>{p('État')}</th>
              <th>{p('Cible des ventes')}</th>
              <th>{p('Créée')}</th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.id} className={v.is_current ? 'is-selected' : ''}>
                <td>
                  <span className="pvp-nom">
                    <button type="button" className="pvp-dot" disabled={!canManage}
                            style={{ '--plan-c': `var(--color-plan-${v.color || 'slate'})` }}
                            title={p('Couleur')} aria-label={p('Couleur')}
                            onClick={() => setPalette(palette === v.id ? null : v.id)} />
                    {palette === v.id ? (
                      <span className="pvp-colors">
                        {PLAN_COLORS.map((c) => {
                          const on = (v.color || 'slate') === c.key;
                          return (
                            <button key={c.key} type="button"
                                    className={`pvp-swatch${on ? ' is-on' : ''}`}
                                    style={{ '--plan-c': `var(--color-plan-${c.key})` }}
                                    title={p(c.label)} aria-label={p(c.label)} aria-pressed={on}
                                    onClick={async () => {
                                      setPalette(null);
                                      if (!on) await done(() => patchPlanVersion(tenantId, v.id, { color: c.key === 'slate' ? '' : c.key }));
                                    }} />
                          );
                        })}
                      </span>
                    ) : (
                      <Cell value={v.label} canWrite={canManage && !v.is_locked}
                            onSave={(label) => done(() => patchPlanVersion(tenantId, v.id, { label }))} />
                    )}
                    {canManage && !v.is_locked && palette !== v.id && (
                      <button type="button" className="lk-edit" title={p('Modifier la version')}
                              onClick={() => ouvrir('edit', v)}>✎</button>
                    )}
                    {v.is_current ? <span className="pvp-current">{p('Courante')}</span>
                      : canManage && (
                        <button type="button" className="lk-btn lk-btn--quiet pvp-surv"
                                onClick={() => done(() => patchPlanVersion(tenantId, v.id, { is_current: true }))}>
                          {p('Définir courante')}
                        </button>
                      )}
                  </span>
                </td>
                <td>
                  {typeLu(v)}
                  {canManage && v.actual_months === 0 && !v.is_reference && (
                    <button type="button" className="lk-btn lk-btn--quiet pvp-surv"
                            onClick={() => done(() => patchPlanVersion(tenantId, v.id, { is_reference: true }))}>
                      {p('Budget de référence')}
                    </button>
                  )}
                </td>
                <td>{v.start_year} → {v.end_year}</td>
                <td>
                  {canManage ? (
                    <button type="button" className="lk-btn lk-btn--quiet pvp-lock"
                            onClick={() => done(() => patchPlanVersion(tenantId, v.id, { is_locked: !v.is_locked }))}>
                      {v.is_locked ? <Lock size={13} /> : <LockOpen size={13} />}
                      {v.is_locked ? p('Verrouillée') : p('Ouverte')}
                    </button>
                  ) : (
                    <span className="pvp-state">
                      {v.is_locked ? <Lock size={13} /> : <LockOpen size={13} />}
                      {v.is_locked ? p('Verrouillée') : p('Ouverte')}
                    </span>
                  )}
                </td>
                {/* LA CIBLE DES VENTES (décision d'Olivier, 2026-10-10) : la
                    Trajectoire de ce plan, ou la Trajectoire (l'objectif figé)
                    ou les Prévisions d'un autre plan. Une copie d'un plan
                    verrouillé vise d'office sa Trajectoire ; les Prévisions et
                    la Synthèse la lisent. */}
                <td>
                  <Choix value={v.sales_target ? `${v.sales_target_source || 'forecast'}:${v.sales_target}` : 'trajectory'}
                         canWrite={canManage}
                         options={[{ value: 'trajectory', label: p('Trajectoire de ce plan') },
                                   ...rows.filter((x) => x.id !== v.id).flatMap((x) => [
                                     { value: `trajectory:${x.id}`, label: `${p('Trajectoire')} · ${x.label}` },
                                     { value: `forecast:${x.id}`, label: `${p('Prévisions')} · ${x.label}` }])]}
                         onSave={(t) => done(() => patchPlanVersion(tenantId, v.id, { sales_target: t || 'trajectory' }))} />
                </td>
                {/* Créée : la date, puis qui (nom figé à la création) ; inconnu
                    pour les versions d'avant et celle née d'office. */}
                <td className="pvp-date">
                  {formatDate(v.created_at)}, {v.created_by_name || <span className="pvp-na">{p('n/a')}</span>}
                </td>
                <td>
                  {canManage && (
                    <button type="button" className="lk-btn lk-btn--quiet" onClick={() => ouvrir('dup', v)}>
                      {p('Dupliquer')}
                    </button>
                  )}
                </td>
                <td>
                  {canManage && !v.is_current && !v.is_locked && (
                    <button type="button" className="lk-x lk-del" aria-label={p('Supprimer')}
                            onClick={() => {
                              if (window.confirm(p('Supprimer cette version et tout ce qu’elle porte ?'))) {
                                done(() => deletePlanVersion(tenantId, v.id));
                              }
                            }}>✕</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </CardShell>

      {fenetre && (
        <div className="pvp-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) setFenetre(null); }}>
          <div className="pvp-dlg" role="dialog" aria-modal="true" aria-labelledby="pvp-dlg-tt">
            <div className="pvp-dlg__h">
              <h2 id="pvp-dlg-tt">
                {fenetre.mode === 'new' ? p('Nouvelle version')
                  : fenetre.mode === 'dup' ? `${p('Dupliquer')} « ${(rows.find((x) => x.id === fenetre.id) || {}).label} »`
                  : p('Modifier la version')}
              </h2>
              <button type="button" className="pvp-dlg__x" aria-label={p('Fermer')} onClick={() => setFenetre(null)}>✕</button>
            </div>
            <div className="pvp-dlg__b">
              <label htmlFor="pvp-nom">{p('Nom')}</label>
              <input id="pvp-nom" className="pvp-in" value={fenetre.label}
                     onChange={(e) => setFenetre({ ...fenetre, label: e.target.value })} />
              {fenetre.mode !== 'edit' && (
                <>
                  <span className="pvp-lbl">{p('Copie de')}</span>
                  <Listbox value={fenetre.source} onChange={(s) => setFenetre({ ...fenetre, source: s })}
                           options={rows.map((x) => ({ value: x.id, label: x.label }))} />
                </>
              )}
              <span className="pvp-lbl pvp-lbl--haut">{p('Type')}</span>
              <div className="pvp-types" role="listbox" aria-label={p('Type')}>
                {types.map((o) => (
                  <button key={o.value} type="button" role="option" aria-selected={fenetre.type === o.value}
                          className={`pvp-type${fenetre.type === o.value ? ' is-on' : ''}`}
                          onClick={() => setFenetre({ ...fenetre, type: o.value })}>
                    <span>{o.t}</span><small>{o.s}</small>
                  </button>
                ))}
              </div>
              <span className="pvp-lbl">{p('Exercice de départ')}</span>
              <Listbox value={fenetre.start} onChange={(a) => setFenetre({ ...fenetre, start: Number(a) })}
                       options={annees.map((a) => ({ value: a, label: String(a) }))} />
              <span className="pvp-lbl">{p('Horizon')}</span>
              <Listbox value={fenetre.horizon} onChange={(h) => setFenetre({ ...fenetre, horizon: Number(h) })}
                       options={Array.from({ length: HORIZON_MAX }, (_, k) => k + 1).map((h) => ({
                         value: h,
                         label: `${h} ${h > 1 ? p('ans') : p('an')} ${p('après l’exercice')} · ${fenetre.start} → ${fenetre.start + h}`,
                       }))} />
            </div>
            <div className="pvp-dlg__f">
              <button type="button" className="lk-btn lk-btn--quiet" onClick={() => setFenetre(null)}>{p('Annuler')}</button>
              <button type="button" className="lk-btn" disabled={!fenetre.label.trim()} onClick={valider}>
                {fenetre.mode === 'new' ? p('Créer') : fenetre.mode === 'dup' ? p('Dupliquer') : p('Enregistrer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
